/* eslint-disable */

/**
 * Dependencies:
 * - Custom select component
 *
 * Required translation strings:
 * - addToCart
 * - noStock
 * - noVariant
 * - onlyXLeft
 */

if (!customElements.get('variant-picker')) {
  class VariantPicker extends HTMLElement {
    constructor() {
      super();
      this.section = this.closest('.js-product');
      this.productForm = this.section.querySelector('.js-product-form-main');
      this.optionSelectors = this.querySelectorAll('.option-selector');
      this.data = this.getProductData();
      this.variant = this.getSelectedVariant();
      this.selectedOptions = this.getSelectedOptions();
      this.preSelection = !this.variant && this.selectedOptions.find((o) => o === null) === null;
      this.handleOptionCardPageClick = this.handleOptionCardPageClick.bind(this);
      this.handleOptionCardResize = this.handleOptionCardResize.bind(this);

      this.addEventListener('change', this.handleVariantChange.bind(this));
      this.addEventListener('click', this.handleOptionCardPageClick);
      window.addEventListener('resize', this.handleOptionCardResize);

      // Defer these to allow for quick-add modal to full initialize
      setTimeout(() => {
        this.updateAvailability();
        this.selectSecondAvailableQuantityCard();
        this.updateQuantityCardImages();
        this.updateOptionCardPaging();
        this.updateAddToCartButton();
        this.scrollSelectedOptionsIntoView();
        this.updateQuantityCardDiscounts();
        this.updateSingleQuantityDiscountedPrice();
      });
    }

    /**
     * Selects the second available quantity/size card on the main PDP.
     * This intentionally overrides Shopify's initial or URL-selected quantity option,
     * while preserving the currently selected values for every other option.
     */
    selectSecondAvailableQuantityCard() {
      if (!this.closest('.cc-main-product')) return;

      const quantitySelectorIndex = Array.from(this.optionSelectors).findIndex((selector) => (
        selector.querySelector('[data-revlift-quantity-card]')
      ));

      if (quantitySelectorIndex === -1) return;

      const quantitySelector = this.optionSelectors[quantitySelectorIndex];
      const availableCards = Array.from(quantitySelector.querySelectorAll('[data-revlift-quantity-card]'))
        .filter((card) => {
          const variant = this.getQuantityCardVariant(quantitySelectorIndex, card.dataset.optionValue || '');
          return variant?.available;
        });
      const targetInput = availableCards[1]?.previousElementSibling;

      if (!targetInput?.matches('input.js-option')) return;

      targetInput.checked = true;
      targetInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.handleOptionCardPageClick);
      window.removeEventListener('resize', this.handleOptionCardResize);
    }

    /**
     * Handles 'change' events on the variant picker element.
     * @param {object} evt - Event object.
     */
    handleVariantChange(evt) {
      this.selectedOptions = this.getSelectedOptions();
      this.variant = this.getSelectedVariant();
      this.preSelection = !this.variant && this.selectedOptions.find((o) => o === null) === null;

      if (this.variant) {
        this.updateMedia();
        this.updateUrl(evt);
        this.updateVariantInput();
      }

      this.updateAddToCartButton();
      this.updateAvailability();
      this.updatePrice();
      this.updateSingleQuantityDiscountedPrice();
      this.updateWeight();
      this.updateBarcode();
      this.updateBackorderText();
      this.updatePickupAvailability();
      this.updateSku();
      this.updateMetafieldVisibility();
      this.updateQuantityCardImages();
      this.syncSelectedQuantityCardPrice();
      this.updateQuantityCardDiscounts();
      VariantPicker.updateLabelText(evt);
      this.scrollSelectedOptionIntoView(evt.target);
      this.updateOptionCardPaging();

      if (!this.preSelection) {
        this.dispatchEvent(new CustomEvent('on:variant:change', {
          bubbles: true,
          detail: {
            form: this.productForm,
            variant: this.variant,
            product: this.data.product
          }
        }));
      }
    }

    /**
     * Shows/hides variant metafield elements on the page as necessary
     */
    updateMetafieldVisibility() {
      document.querySelectorAll('[data-variant-metafield]').forEach((elem) => {
        elem.classList.toggle('hidden', elem.dataset.variantMetafield !== this.variant?.id.toString());
      });
    }

    /**
     * Scrolls horizontal option rows so selected chips are visible on load.
     */
    scrollSelectedOptionsIntoView() {
      this.optionSelectors.forEach((selector) => {
        if (selector.dataset.selectorType === 'dropdown') return;

        const selected = selector.querySelector('input:checked');
        this.scrollSelectedOptionIntoView(selected);
      });
    }

    /**
     * Scrolls one horizontal option row so the selected chip aligns left.
     * @param {Element} optionEl - Selected option input.
     */
    scrollSelectedOptionIntoView(optionEl) {
      const selectedLabel = optionEl?.nextElementSibling;
      const row = selectedLabel?.closest('.option-selector__btns');
      if (!row) return;

      if (VariantPicker.isMobileQuantityCardSelector(selectedLabel)) {
        this.updateOptionCardPaging(row);
        return;
      }

      if (VariantPicker.isDesktopCardSelector(row)) {
        this.setOptionCardPageForLabel(row, selectedLabel);
        return;
      }

      if (row.scrollWidth <= row.clientWidth) return;

      const targetLeft = selectedLabel.offsetLeft - row.offsetLeft;
      const maxLeft = row.scrollWidth - row.clientWidth;
      row.scrollTo({
        left: Math.max(0, Math.min(targetLeft, maxLeft)),
        behavior: VariantPicker.prefersReducedMotion() ? 'auto' : 'smooth'
      });
      this.updateOptionCardPaging(row);
    }

    /**
     * Indicates whether motion should be reduced for the current visitor.
     * @returns {boolean}
     */
    static prefersReducedMotion() {
      return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    /**
     * Indicates whether this is a desktop card selector.
     * @param {Element} row - Option button row.
     * @returns {boolean}
     */
    static isDesktopCardSelector(row) {
      return window.matchMedia
        && window.matchMedia('(min-width: 750px)').matches
        && row.classList.contains('option-selector__btns--cards');
    }

    /**
     * Indicates whether this is a mobile quantity card label.
     * @param {Element} label - Selected option label.
     * @returns {boolean}
     */
    static isMobileQuantityCardSelector(label) {
      return window.matchMedia
        && window.matchMedia('(max-width: 749px)').matches
        && label?.classList.contains('opt-label--quantity-card');
    }

    /**
     * Handles desktop card selector pagination.
     * @param {object} evt - Event object.
     */
    handleOptionCardPageClick(evt) {
      const button = evt.target.closest('[data-option-page]');
      if (!button || !this.contains(button)) return;

      const row = button.closest('.option-selector')?.querySelector('.option-selector__btns--cards');
      if (!row) return;

      const direction = button.dataset.optionPage === 'previous' ? -1 : 1;
      const currentPage = Number(row.dataset.optionPageCurrent || 0);
      row.dataset.optionPageCurrent = String(Math.max(0, currentPage + direction));
      this.updateOptionCardPaging(row);
    }

    /**
     * Updates card pagination after responsive size changes.
     */
    handleOptionCardResize() {
      this.updateOptionCardPaging();
    }

    /**
     * Shows pagination controls only when desktop card rows exceed two visible rows.
     * @param {Element} targetRow - Optional row to update.
     */
    updateOptionCardPaging(targetRow = null) {
      const rows = targetRow ? [targetRow] : Array.from(this.querySelectorAll('.option-selector__btns--cards'));

      rows.forEach((row) => {
        const selector = row.closest('.option-selector');
        const isDesktopCardSelector = VariantPicker.isDesktopCardSelector(row);
        const labels = Array.from(row.querySelectorAll('.opt-label--option-card'));

        if (!isDesktopCardSelector) {
          selector?.classList.remove('option-selector--has-pages');
          row.dataset.optionPageCurrent = '0';
          labels.forEach((label) => label.classList.remove('option-card--page-hidden'));

          const previous = selector?.querySelector('[data-option-page="previous"]');
          const next = selector?.querySelector('[data-option-page="next"]');
          if (previous) previous.disabled = true;
          if (next) next.disabled = true;
          return;
        }

        let pageSize = VariantPicker.getOptionCardPageSize(row);
        let hasOverflow = labels.length > pageSize;
        selector?.classList.toggle('option-selector--has-pages', hasOverflow);

        pageSize = VariantPicker.getOptionCardPageSize(row);
        hasOverflow = labels.length > pageSize;
        selector?.classList.toggle('option-selector--has-pages', hasOverflow);

        if (!hasOverflow) {
          row.dataset.optionPageCurrent = '0';
          labels.forEach((label) => label.classList.remove('option-card--page-hidden'));
        }

        const maxPage = Math.max(0, Math.ceil(labels.length / pageSize) - 1);
        const currentPage = Math.max(0, Math.min(Number(row.dataset.optionPageCurrent || 0), maxPage));
        row.dataset.optionPageCurrent = String(currentPage);

        if (hasOverflow) {
          const firstVisibleIndex = currentPage * pageSize;
          const lastVisibleIndex = firstVisibleIndex + pageSize - 1;
          labels.forEach((label, index) => {
            label.classList.toggle('option-card--page-hidden', index < firstVisibleIndex || index > lastVisibleIndex);
          });
        }

        const previous = selector?.querySelector('[data-option-page="previous"]');
        const next = selector?.querySelector('[data-option-page="next"]');
        if (!previous || !next) return;

        previous.disabled = !hasOverflow || currentPage <= 0;
        next.disabled = !hasOverflow || currentPage >= maxPage;
      });
    }

    /**
     * Calculates how many cards fit in two desktop rows.
     * @param {Element} row - Option button row.
     * @returns {number}
     */
    static getOptionCardPageSize(row) {
      const style = window.getComputedStyle(row);
      const cardWidth = parseFloat(style.getPropertyValue('--option-card-width')) || 132;
      const gap = parseFloat(style.getPropertyValue('--option-card-gap')) || 12;
      const cardsPerRow = Math.max(1, Math.floor((row.clientWidth + gap) / (cardWidth + gap)));
      return cardsPerRow * 2;
    }

    /**
     * Pages desktop card selectors to the selected label.
     * @param {Element} row - Option button row.
     * @param {Element} selectedLabel - Selected option label.
     */
    setOptionCardPageForLabel(row, selectedLabel) {
      const labels = Array.from(row.querySelectorAll('.opt-label--option-card'));
      const selectedIndex = labels.indexOf(selectedLabel);
      if (selectedIndex === -1) return;

      const pageSize = VariantPicker.getOptionCardPageSize(row);
      row.dataset.optionPageCurrent = String(Math.floor(selectedIndex / pageSize));
      this.updateOptionCardPaging(row);
    }

    /**
     * Updates quantity card images to match the currently selected non-quantity options.
     */
    updateQuantityCardImages() {
      const quantitySelectorIndex = Array.from(this.optionSelectors).findIndex((selector) => {
        const label = selector.querySelector('.label');
        return label?.textContent.trim().toLowerCase().includes('quantity');
      });

      if (quantitySelectorIndex === -1) return;

      const quantitySelector = this.optionSelectors[quantitySelectorIndex];
      const quantityCards = quantitySelector.querySelectorAll('.opt-label--quantity-card[data-option-value]');
      if (!quantityCards.length) return;

      quantityCards.forEach((card) => {
        const quantityValue = card.dataset.optionValue;
        const matchingVariant = this.data.product.variants.find((variant) => (
          variant.options[quantitySelectorIndex] === quantityValue
          && variant.options.every((optionValue, optionIndex) => (
            optionIndex === quantitySelectorIndex
            || this.selectedOptions[optionIndex] === null
            || optionValue === this.selectedOptions[optionIndex]
          ))
          && variant.featured_media?.preview_image?.src
        ));

        if (!matchingVariant) return;

        const image = matchingVariant.featured_media.preview_image;
        const media = card.querySelector('.opt-label__quantity-media');
        if (!media) return;

        let img = media.querySelector('img');
        if (!img) {
          img = document.createElement('img');
          img.className = 'img-fit img-fit--contain';
          img.loading = 'lazy';
          media.appendChild(img);
        }

        const src = VariantPicker.imageUrlWithWidth(image.src, 180);
        img.src = src;
        img.srcset = `${VariantPicker.imageUrlWithWidth(image.src, 180)} 1x, ${VariantPicker.imageUrlWithWidth(image.src, 360)} 2x`;
        img.width = 180;
        img.height = Math.round(180 / (image.aspect_ratio || 1));
      });
    }

    /**
     * Updates quantity option cards with RevLift tier discounts for the current page.
     */
    async updateQuantityCardDiscounts() {
      const quantitySelectorIndex = Array.from(this.optionSelectors).findIndex((selector) => {
        const label = selector.querySelector('.label');
        return label?.textContent.trim().toLowerCase().includes('quantity');
      });

      if (quantitySelectorIndex === -1) return;

      const quantitySelector = this.optionSelectors[quantitySelectorIndex];
      const quantityCards = Array.from(quantitySelector.querySelectorAll('[data-revlift-quantity-card]'));
      if (!quantityCards.length) return;

      const quantityCardVariants = quantityCards.map((card) => {
        const quantity = VariantPicker.parseQuantityFromOptionValue(card.dataset.optionValue || '');
        const variant = this.getQuantityCardVariant(
          quantitySelectorIndex,
          card.dataset.optionValue || ''
        );
        return { card, quantity, variant };
      });
      const baseQuantityCard = quantityCardVariants
        .filter(({ quantity, variant }) => quantity > 0 && Number(variant?.price || 0) > 0)
        .sort((a, b) => a.quantity - b.quantity)[0];
      const baseUnitCents = baseQuantityCard
        ? Number(baseQuantityCard.variant.price) / baseQuantityCard.quantity
        : 0;

      quantityCardVariants.forEach(({ card, quantity, variant }) => {
        const percentOff = VariantPicker.quantityCardDiscountPercent(
          variant,
          quantity,
          baseUnitCents
        );
        this.applyProductDiscountToQuantityCard(card, variant, baseUnitCents, percentOff);
      });

      this.syncSelectedQuantityCardPrice();
    }

    /**
     * Finds the variant represented by a quantity card under the current selected options.
     * @param {number} quantitySelectorIndex - Quantity option index.
     * @param {string} quantityValue - Quantity option value.
     * @returns {object|null}
     */
    getQuantityCardVariant(quantitySelectorIndex, quantityValue) {
      return this.data.product.variants.find((variant) => (
        variant.options[quantitySelectorIndex] === quantityValue
        && variant.options.every((optionValue, optionIndex) => (
          optionIndex === quantitySelectorIndex
          || this.selectedOptions[optionIndex] === null
          || optionValue === this.selectedOptions[optionIndex]
        ))
      )) || null;
    }

    /**
     * Fetches the RevLift app config once for this picker.
     * @returns {Promise<object|null>}
     */
    getRevliftConfig() {
      if (window.__revliftQuantityCardConfig) {
        this.revliftConfigPayload = window.__revliftQuantityCardConfig;
        return Promise.resolve(this.revliftConfigPayload);
      }

      if (!this.revliftConfigPromise) {
        const shop = encodeURIComponent(window.Shopify?.shop || '');
        this.revliftConfigPromise = fetch(`/apps/everything-app?shop=${shop}&ts=${Date.now()}`)
          .then((response) => (response.ok ? response.json() : null))
          .then((payload) => {
            this.revliftConfigPayload = payload;
            return payload;
          })
          .catch(() => null);
      }

      return this.revliftConfigPromise;
    }

    /**
     * Finds the RevLift offer configured for the current PDP path.
     * @returns {Promise<object|null>}
     */
    async getCurrentRevliftPageOffer() {
      const payload = await this.getRevliftConfig();
      const offers = payload?.config?.pageOffers || [];
      return offers.find((offer) => (
        (offer.pagePaths || []).some((pattern) => VariantPicker.pagePathMatches(pattern))
      )) || null;
    }

    /**
     * Finds a RevLift product offer matching this product's variants or collections.
     * @param {Array} variantIds - Variant IDs to test against offer targets.
     * @returns {Promise<object|null>}
     */
    async getCurrentRevliftProductOffer(variantIds = []) {
      const payload = await this.getRevliftConfig();
      const offers = payload?.config?.productOffers || [];
      const targetVariantIds = variantIds.length ? variantIds : [this.variant?.id];
      return this.findCurrentRevliftProductOffer(offers, targetVariantIds);
    }

    /**
     * Finds a RevLift product offer from a provided offer list.
     * @param {Array} offers - RevLift product offers.
     * @param {Array} variantIds - Variant IDs to test against offer targets.
     * @returns {object|null}
     */
    findCurrentRevliftProductOffer(offers, variantIds = []) {
      const collectionIds = new Set((this.data.collectionIds || []).map((id) => String(id)));
      const targetVariantIds = new Set(variantIds.filter(Boolean).map((id) => String(id)));

      return offers.find((offer) => (
        VariantPicker.offerTargetsVariant(offer, targetVariantIds)
        ||
        (offer.collectionTargets || []).some((id) => collectionIds.has(String(id)))
      )) || null;
    }

    /**
     * Checks whether a RevLift offer targets one of the given variants.
     * @param {object} offer - RevLift product offer.
     * @param {Set} variantIds - Variant IDs to match.
     * @returns {boolean}
     */
    static offerTargetsVariant(offer, variantIds) {
      if (!variantIds?.size) return false;

      const targets = offer?.targets || [];
      if (targets.some((id) => variantIds.has(String(id)))) return true;

      return (offer?.conditions?.items || []).some((item) => (
        variantIds.has(String(item.variantId))
      ));
    }

    /**
     * Finds the RevLift product offer whose tiers match the available quantity cards.
     * @param {Element[]} quantityCards - Quantity card labels.
     * @returns {Promise<object|null>}
     */
    async getCurrentRevliftQuantityOffer(quantityCards) {
      const payload = await this.getRevliftConfig();
      const offers = payload?.config?.productOffers || [];
      return this.findCurrentRevliftQuantityOffer(quantityCards, offers);
    }

    /**
     * Finds the RevLift product offer whose tiers match the available quantity cards.
     * @param {Element[]} quantityCards - Quantity card labels.
     * @param {Array} offers - RevLift product offers.
     * @returns {object|null}
     */
    findCurrentRevliftQuantityOffer(quantityCards, offers) {
      const quantities = new Set(
        quantityCards
          .map((card) => VariantPicker.parseQuantityFromOptionValue(card.dataset.optionValue || ''))
          .filter((quantity) => quantity > 0)
      );

      if (!quantities.size) return null;

      return offers
        .map((offer) => {
          const tiers = offer.tiers || [];
          const matchingTiers = tiers.filter((tier) => quantities.has(Number(tier.minQuantity || 0)));
          const allTiersMatch = matchingTiers.length === tiers.length;
          return { offer, matchingTiers, allTiersMatch };
        })
        .filter((match) => match.allTiersMatch && match.matchingTiers.length > 0)
        .sort((a, b) => b.matchingTiers.length - a.matchingTiers.length)
        [0]?.offer || null;
    }

    /**
     * Updates the PDP price for products that have one sellable quantity and a RevLift product discount.
     */
    async updateSingleQuantityDiscountedPrice() {
      if (!this.variant || this.querySelector('[data-revlift-quantity-card]')) return;

      this.revliftSingleQuantityPriceRequestId = (this.revliftSingleQuantityPriceRequestId || 0) + 1;
      const requestId = this.revliftSingleQuantityPriceRequestId;
      const offer = await this.getCurrentRevliftProductOffer();
      if (this.revliftSingleQuantityPriceRequestId !== requestId) return;

      const tier = VariantPicker.discountForQuantity(offer, 1);
      const percentOff = Number(tier?.percentOff || 0);
      const baseCents = Number(this.variant.price || 0);
      if (!Number.isFinite(percentOff) || percentOff <= 0 || !baseCents) return;

      const discountedCents = baseCents - Math.floor(baseCents * Math.min(100, percentOff) / 100 + 1e-7);
      if (discountedCents >= baseCents) return;

      this.price = this.price || this.section.querySelector('.product-info__price > .price');
      const priceCurrentEl = this.price?.querySelector('.price__current');
      const priceWasEl = this.price?.querySelector('.price__was');
      if (!priceCurrentEl || !priceWasEl) return;

      priceCurrentEl.querySelector('.js-label').textContent = theme.strings.salePrice;
      priceCurrentEl.querySelector('.js-value').innerHTML = VariantPicker.formatCentsWithCurrency(discountedCents);
      priceWasEl.querySelector('.js-label').textContent = theme.strings.regularPrice;
      priceWasEl.querySelector('.js-value').innerHTML = this.data.formatted[this.variant.id].price;
      this.updateSaleDiscountBadge(discountedCents, baseCents);
      this.price.classList.add('price--on-sale');
    }

    /**
     * Checks whether the current path matches a RevLift page path pattern.
     * @param {string} pattern - Exact path or trailing-star prefix pattern.
     * @returns {boolean}
     */
    static pagePathMatches(pattern) {
      if (!pattern) return false;
      if (pattern.endsWith('*')) return window.location.pathname.startsWith(pattern.slice(0, -1));
      return window.location.pathname === pattern;
    }

    /**
     * Applies the product's built-in quantity pricing to a visible quantity card.
     * @param {Element} card - Quantity card label.
     * @param {object|null} variant - Matching Shopify variant.
     * @param {number} baseUnitCents - Per-unit price of the smallest quantity variant.
     * @param {number} percentOff - Discount percent calculated from the variant price.
     */
    applyProductDiscountToQuantityCard(card, variant, baseUnitCents, percentOff) {
      const discountEl = card.querySelector('[data-revlift-quantity-discount]');
      const priceEl = card.querySelector('[data-revlift-quantity-price]');
      const formattedPrice = variant ? this.data.formatted?.[variant.id]?.price : '';
      const priceCents = Number(variant?.price || 0);
      const quantity = VariantPicker.parseQuantityFromOptionValue(card.dataset.optionValue || '');
      const unitQuantity = quantity > 0 ? quantity : 1;
      const unitLabel = VariantPicker.quantityCardUnitLabel(card.dataset.optionValue || '');
      const unitPriceCents = Math.round(priceCents / unitQuantity);
      const compareAtCents = percentOff > 0
        ? Math.round(baseUnitCents * unitQuantity)
        : 0;
      const totalPrice = priceCents
        ? VariantPicker.formatCentsWithCurrency(priceCents)
        : VariantPicker.stripCurrencyCode(formattedPrice);
      const unitPrice = priceCents
        ? VariantPicker.formatCentsWithCurrency(unitPriceCents)
        : '';

      card.dataset.revliftQuantity = quantity ? String(quantity) : '';
      card.dataset.revliftVariantId = variant?.id ? String(variant.id) : '';
      card.dataset.revliftQuantityPriceCents = '';
      card.dataset.revliftQuantityCompareAtCents = '';
      card.dataset.revliftQuantityPriceHtml = '';
      card.dataset.revliftQuantityCompareAtHtml = '';
      card.dataset.revliftQuantityDiscountPercent = '';

      console.log('[RevLift PDP quantity discount]', {
        variantId: variant?.id ? String(variant.id) : '',
        quantityOption: card.dataset.optionValue || '',
        parsedQuantity: quantity || null,
        priceCents,
        baseUnitCents,
        compareAtCents,
        assignedDiscountPercent: Number.isFinite(percentOff) ? percentOff : 0,
      });

      if (!discountEl || !priceEl) return;

      discountEl.classList.remove('opt-label__quantity-discount--loading');
      priceEl.classList.remove('opt-label__quantity-price--loading');
      discountEl.removeAttribute('aria-hidden');
      priceEl.removeAttribute('aria-hidden');

      if (!variant || !priceCents) {
        discountEl.hidden = true;
        discountEl.textContent = '';
        priceEl.hidden = !formattedPrice;
        priceEl.innerHTML = totalPrice
          ? VariantPicker.quantityCardPriceHtml(totalPrice, unitPrice, unitLabel)
          : '';
        if (formattedPrice) {
          card.dataset.revliftQuantityPriceCents = priceCents ? String(priceCents) : '';
          card.dataset.revliftQuantityPriceHtml = totalPrice;
        }
        card.dataset.revliftQuantityDiscountPercent = '0';
        card.classList.remove('has-revlift-discount');
        return;
      }

      if (!Number.isFinite(percentOff) || percentOff <= 0) {
        discountEl.hidden = true;
        discountEl.textContent = '';
        priceEl.hidden = !formattedPrice;
        priceEl.innerHTML = totalPrice
          ? VariantPicker.quantityCardPriceHtml(totalPrice, unitPrice, unitLabel)
          : '';
        if (formattedPrice) {
          card.dataset.revliftQuantityPriceCents = priceCents ? String(priceCents) : '';
          card.dataset.revliftQuantityPriceHtml = totalPrice;
        }
        card.dataset.revliftQuantityDiscountPercent = '0';
        card.classList.remove('has-revlift-discount');
        return;
      }

      const comparePrice = VariantPicker.formatCentsWithCurrency(compareAtCents);

      discountEl.textContent = `Save ${percentOff}%`;
      discountEl.hidden = false;
      priceEl.innerHTML = VariantPicker.quantityCardPriceHtml(
        totalPrice,
        unitPrice,
        unitLabel
      );
      priceEl.hidden = false;
      card.dataset.revliftQuantityPriceCents = String(priceCents);
      card.dataset.revliftQuantityCompareAtCents = String(compareAtCents);
      card.dataset.revliftQuantityPriceHtml = totalPrice;
      card.dataset.revliftQuantityCompareAtHtml = comparePrice;
      card.dataset.revliftQuantityDiscountPercent = String(percentOff);
      card.classList.add('has-revlift-discount');
    }

    /**
     * Calculates a quantity card's savings from its Shopify variant price.
     * @param {object|null} variant - Matching Shopify variant.
     * @param {number} quantity - Quantity represented by the card.
     * @param {number} baseUnitCents - Per-unit price of the smallest quantity variant.
     * @returns {number}
     */
    static quantityCardDiscountPercent(variant, quantity, baseUnitCents) {
      const priceCents = Number(variant?.price || 0);
      const compareAtCents = Number(baseUnitCents || 0) * Number(quantity || 0);
      if (!priceCents || !compareAtCents || priceCents >= compareAtCents) return 0;
      return Math.round(((compareAtCents - priceCents) * 100) / compareAtCents);
    }

    /**
     * Mirrors the selected quantity card price into the hidden PDP price node.
     * RevLift's subscription widget uses the theme price as its price source.
     */
    syncSelectedQuantityCardPrice() {
      const selectedCard = this.querySelector('input:checked + [data-revlift-quantity-card]');
      if (!selectedCard) return;

      const priceHtml = selectedCard.dataset.revliftQuantityPriceHtml;
      if (!priceHtml) return;

      this.price = this.price || this.section.querySelector('.product-info__price > .price');
      const priceCurrentEl = this.price?.querySelector('.price__current');
      const priceWasEl = this.price?.querySelector('.price__was');
      if (!priceCurrentEl || !priceWasEl) return;

      const compareAtHtml = selectedCard.dataset.revliftQuantityCompareAtHtml || '';
      const priceCents = Number(selectedCard.dataset.revliftQuantityPriceCents || 0);
      const compareAtCents = Number(selectedCard.dataset.revliftQuantityCompareAtCents || 0);
      priceCurrentEl.querySelector('.js-label').textContent = compareAtHtml ? theme.strings.salePrice : theme.strings.regularPrice;
      priceCurrentEl.querySelector('.js-value').innerHTML = priceHtml;
      priceWasEl.querySelector('.js-label').textContent = compareAtHtml ? theme.strings.regularPrice : '';
      priceWasEl.querySelector('.js-value').innerHTML = compareAtHtml;
      this.updateSaleDiscountBadge(priceCents, compareAtCents);
      this.price.classList.toggle('price--on-sale', Boolean(compareAtHtml));
      this.price.dataset.revliftQuantityPriceSource = 'selected-card';
      this.price.dataset.revliftQuantityPriceCents = priceCents ? String(priceCents) : '';

      // The app owns purchase-option prices and uses the selected selling-plan allocation.
      document.dispatchEvent(new Event('revlift:refresh'));
    }

    /**
     * Finds the best tier for a quantity.
     * @param {object|null} offer - RevLift offer.
     * @param {number} quantity - Parsed quantity.
     * @returns {object|null}
     */
    static discountForQuantity(offer, quantity) {
      if (!quantity) return null;
      const tiers = [...(offer?.tiers || [])].sort((a, b) => Number(b.minQuantity || 0) - Number(a.minQuantity || 0));
      return tiers.find((tier) => quantity >= Number(tier.minQuantity || 0)) || null;
    }

    /**
     * Parses the purchased quantity from an option label such as "10 Rolls".
     * @param {string} value - Quantity option value.
     * @returns {number}
     */
    static parseQuantityFromOptionValue(value) {
      const match = String(value || '').replace(/,/g, '').match(/\d+(?:\.\d+)?/);
      const quantity = match ? Number(match[0]) : 0;
      return Number.isFinite(quantity) ? quantity : 0;
    }

    /**
     * Gets a singular unit name from a quantity option such as "4 Sleeves".
     * @param {string} value - Quantity option value.
     * @returns {string}
     */
    static quantityCardUnitLabel(value) {
      const match = String(value || '').match(/\d+(?:\.\d+)?\s*([a-z-]+)/i);
      let unit = String(match?.[1] || 'item').toLowerCase();

      if (unit.endsWith('ies')) {
        unit = `${unit.slice(0, -3)}y`;
      } else if (unit.endsWith('s') && !unit.endsWith('ss')) {
        unit = unit.slice(0, -1);
      }

      return unit;
    }

    /**
     * Builds the per-unit and total price hierarchy for a quantity card.
     * @param {string} totalPrice - Current total price.
     * @param {string} unitPrice - Current per-unit price.
     * @param {string} unitLabel - Singular unit label.
     * @returns {string}
     */
    static quantityCardPriceHtml(totalPrice, unitPrice, unitLabel) {
      const unitHtml = unitPrice
        ? `<span class="opt-label__quantity-unit"><span class="opt-label__quantity-unit-price">${unitPrice}</span><span class="opt-label__quantity-unit-label">/ ${unitLabel}</span></span>`
        : `<span class="opt-label__quantity-unit"><span class="opt-label__quantity-unit-price">${totalPrice}</span></span>`;
      const totalHtml = unitPrice
        ? `<span class="opt-label__quantity-total">${totalPrice} total</span>`
        : '';

      return `${unitHtml}${totalHtml}`;
    }

    /**
     * Parses a percentage from display text such as "10% Off".
     * @param {string} value - Text containing a percent.
     * @returns {number}
     */
    static parsePercent(value) {
      const match = String(value || '').match(/(\d+(?:\.\d+)?)\s*%/);
      const percent = match ? Number(match[1]) : 0;
      return Number.isFinite(percent) ? percent : 0;
    }

    /**
     * Formats a cents integer for display.
     * @param {number} cents - Price in cents.
     * @returns {string}
     */
    static formatCents(cents) {
      const amount = Math.round(Number(cents || 0)) / 100;
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: window.Shopify?.currency?.active || 'USD',
        minimumFractionDigits: Number.isInteger(amount) ? 0 : 2,
        maximumFractionDigits: 2
      }).format(amount);
    }

    /**
     * Formats a cents integer using the active currency symbol.
     * @param {number} cents - Price in cents.
     * @returns {string}
     */
    static formatCentsWithCurrency(cents) {
      return VariantPicker.formatCents(cents);
    }

    /**
     * Removes a trailing currency code from money HTML/text.
     * @param {string} value - Formatted money value.
     * @returns {string}
     */
    static stripCurrencyCode(value) {
      return String(value || '').replace(/\s*(USD|CAD|AUD|NZD|GBP|EUR)\s*$/i, '').trim();
    }

    /**
     * Adds/replaces a Shopify image width parameter.
     * @param {string} src - Shopify image URL.
     * @param {number} width - Requested image width.
     * @returns {string}
     */
    static imageUrlWithWidth(src, width) {
      const url = src.startsWith('//') ? `https:${src}` : src;
      const [base, query = ''] = url.split('?');
      const params = new URLSearchParams(query);
      params.set('width', width);
      return `${base}?${params.toString()}`;
    }

    /**
     * Updates the "Add to Cart" button label and disabled state.
     */
    updateAddToCartButton() {
      this.productForm = this.section.querySelector('.js-product-form-main');
      if (!this.productForm) return;

      this.addBtn = this.addBtn || this.productForm.querySelector('[name="add"]');
      const variantAvailable = this.variant && this.variant.available;

      this.addBtn.disabled = !variantAvailable || this.preSelection;

      const enhancedAtcLabel = this.addBtn.querySelector('.js-enhanced-atc-label');
      const enhancedAtcFallback = this.addBtn.querySelector('.js-enhanced-atc-fallback');
      if (enhancedAtcLabel && enhancedAtcFallback) {
        const showEnhancedLabel = Boolean(variantAvailable && !this.preSelection);
        enhancedAtcLabel.hidden = !showEnhancedLabel;
        enhancedAtcFallback.hidden = showEnhancedLabel;

        if (showEnhancedLabel) {
          const priceEl = enhancedAtcLabel.querySelector('.js-enhanced-atc-price');
          const comparePriceEl = enhancedAtcLabel.querySelector('.js-enhanced-atc-compare-price');
          const formatted = this.data.formatted[this.variant.id] || {};

          if (priceEl && formatted.price) priceEl.innerHTML = formatted.price;
          if (comparePriceEl) {
            comparePriceEl.innerHTML = formatted.compareAtPrice || '';
            comparePriceEl.hidden = !formatted.compareAtPrice;
          }
        } else {
          const unavailableStr = this.variant ? theme.strings.noStock : theme.strings.noVariant;
          enhancedAtcFallback.textContent = this.preSelection
            ? theme.strings.noSelectedVariant
            : unavailableStr;
        }
        return;
      }

      if (this.preSelection) {
        this.addBtn.textContent = theme.strings.noSelectedVariant;
      } else {

        const unavailableStr = this.variant ? theme.strings.noStock : theme.strings.noVariant;
        this.addBtn.textContent = variantAvailable
          ? this.addBtn.dataset.addToCartText
          : unavailableStr;
      }
    }

    /**
     * Updates the availability status of an option.
     * @param {Element} optionEl - Option element.
     * @param {boolean} exists - Does this option lead to a variant that exists?
     * @param {boolean} available - Does this option lead to a variant that is available to buy?
     */
    static updateOptionAvailability(optionEl, exists, available) {
      const el = optionEl;
      const unavailableText = exists ? theme.strings.noStock : theme.strings.noVariant;
      el.classList.toggle('is-unavailable', !available);
      el.classList.toggle('is-nonexistent', !exists);

      if (optionEl.classList.contains('custom-select__option')) {
        const em = el.querySelector('em');

        if (em) {
          em.hidden = available;
        }

        if (!available) {
          if (em) {
            em.textContent = unavailableText;
          } else {
            el.innerHTML = `${el.innerHTML} <em class="pointer-events-none">${unavailableText}</em>`;
          }
        }
      } else if (!available) {
        el.nextElementSibling.title = unavailableText;
      } else {
        el.nextElementSibling.removeAttribute('title');
      }
    }

    /**
     * Updates the availability status in option selectors.
     */
    updateAvailability() {
      if (this.dataset.showAvailability === 'false') return;
      const { availabilityMethod } = this.dataset; // 'downward' or 'selection'
      let currVariant = this.variant;

      if (!this.variant) {
        currVariant = { options: this.selectedOptions };
      }

      if (availabilityMethod === 'selection') {
        // Flag all options as unavailable
        this.querySelectorAll('.js-option').forEach((optionEl) => {
          VariantPicker.updateOptionAvailability(optionEl, false, false);
        });

        // Flag selector options as available or sold out, depending on the variant availability
        this.optionSelectors.forEach((selector, selectorIndex) => {
          this.data.product.variants.forEach((variant) => {
            let matchCount = 0;

            variant.options.forEach((option, optionIndex) => {
              if (option === currVariant.options[optionIndex] && optionIndex !== selectorIndex) {
                matchCount += 1;
              }
            });

            if (matchCount === currVariant.options.length - 1) {
              const options = selector.querySelectorAll('.js-option');
              const optionEl = Array.from(options).find((opt) => {
                if (selector.dataset.selectorType === 'dropdown') {
                  return opt.dataset.value === variant.options[selectorIndex];
                }
                return opt.value === variant.options[selectorIndex];
              });

              if (optionEl) {
                VariantPicker.updateOptionAvailability(optionEl, true, variant.available);
              }
            }
          });
        });
      } else {
        this.optionSelectors.forEach((selector, selectorIndex) => {
          const options = selector.querySelectorAll('.js-option:not([data-value=""])');
          options.forEach((option) => {
            const optionValue = selector.dataset.selectorType === 'dropdown' ? option.dataset.value : option.value;
            // any available variants with previous options and this one locked in?
            let variantsExist = false;
            let variantsAvailable = false;
            this.data.product.variants.forEach((v) => {
              let matches = 0;
              for (let i = 0; i < selectorIndex; i += 1) {
                if (v.options[i] === this.selectedOptions[i] || this.selectedOptions[i] === null) {
                  matches += 1;
                }
              }
              if (v.options[selectorIndex] === optionValue && matches === selectorIndex) {
                variantsExist = true;
                if (v.available) {
                  variantsAvailable = true;
                }
              }
            });
            VariantPicker.updateOptionAvailability(option, variantsExist, variantsAvailable);
          });
        });
      }
    }

    /**
     * Updates the backorder text and visibility.
     */
    updateBackorderText() {
      this.backorder = this.backorder || this.section.querySelector('.backorder');
      if (!this.backorder) return;

      let hideBackorder = true;

      if (this.variant && this.variant.available) {
        const { inventory } = this.data.formatted[this.variant.id];

        if (this.variant.inventory_management && inventory === 'none') {
          const backorderProdEl = this.backorder.querySelector('.backorder__product');
          const prodTitleEl = this.section.querySelector('.product-title');
          const variantTitle = this.variant.title.includes('Default')
            ? ''
            : ` - ${this.variant.title}`;

          backorderProdEl.textContent = `${prodTitleEl.textContent}${variantTitle}`;
          hideBackorder = false;
        }
      }

      this.backorder.hidden = hideBackorder;
    }

    /**
     * Updates the option label selected value text.
     * @param {object} evt - Event object
     */
    static updateLabelText(evt) {
      const selector = evt.target.closest('.option-selector');
      if (selector.dataset.selectorType === 'dropdown') return;

      const labelValue = selector.querySelector('.js-color-text, .js-quantity-text, .js-label-text');
      if (!labelValue) return;

      const selectedLabel = evt.target.nextElementSibling;
      labelValue.textContent = selectedLabel.dataset.labelValue || selectedLabel.querySelector('.js-value').textContent;
    }

    /**
     * Updates the product media.
     */
    updateMedia() {
      if (!this.variant.featured_media) return;

      if (this.section.matches('quick-add-drawer')) {
        this.section.updateMedia(this.variant.featured_media.id);
      } else {
        this.mediaGallery = this.mediaGallery || this.section.querySelector('media-gallery');
        if (!this.mediaGallery) return;

        const variantMedia = this.mediaGallery.querySelector(
          `[data-media-id="${this.variant.featured_media.id}"]`
        );
        this.mediaGallery.setActiveMedia(variantMedia, true, true);
      }
    }

    /**
     * Updates the pick up availability.
     */
    updatePickupAvailability() {
      this.pickUpAvailability =
        this.pickUpAvailability || this.section.querySelector('pickup-availability');
      if (!this.pickUpAvailability) return;

      if (this.variant && this.variant.available) {
        this.pickUpAvailability.getAvailability(this.variant.id);
      } else {
        this.pickUpAvailability.removeAttribute('available');
        this.pickUpAvailability.innerHTML = '';
      }
    }

    /**
     * Updates the price.
     */
    updatePrice() {
      this.price = this.price || this.section.querySelector('.product-info__price > .price');
      if (!this.price) return;

      let { variant } = this;
      if (this.preSelection) {
        variant = this.data.product.variants[0];
        for (let i = 1; i < this.data.product.variants.length; i += 1) {
          if (this.data.product.variants[i].price < variant.price) variant = this.data.product.variants[i];
        }
      }

      if (this.variant) {
        const priceCurrentEl = this.price.querySelector('.price__current');
        const priceWasEl = this.price.querySelector('.price__was');
        const unitPriceEl = this.price.querySelector('.unit-price');

        // Update price.
        if (variant.compare_at_price > variant.price) {
          priceCurrentEl.querySelector('.js-label').textContent = theme.strings.salePrice;
          priceCurrentEl.querySelector('.js-value').innerHTML = this.data.formatted[variant.id].price;
          if (priceWasEl) {
            priceWasEl.querySelector('.js-label').textContent = theme.strings.regularPrice;
            priceWasEl.querySelector('.js-value').innerHTML = this.data.formatted[variant.id].compareAtPrice || '';
          }
          this.updateSaleDiscountBadge(variant.price, variant.compare_at_price);
        } else {
          priceCurrentEl.querySelector('.js-label').textContent = theme.strings.regularPrice;
          priceCurrentEl.querySelector('.js-value').innerHTML = this.data.formatted[variant.id].price;
          if (priceWasEl) {
            priceWasEl.querySelector('.js-label').textContent = '';
            priceWasEl.querySelector('.js-value').innerHTML = '';
          }
          this.updateSaleDiscountBadge(0, 0);
        }

        // Update unit price, if specified.
        if (this.variant.unit_price_measurement) {
          const valueEl = this.price.querySelector('.unit-price__price');
          const unitEl = this.price.querySelector('.unit-price__unit');
          const value = this.variant.unit_price_measurement.reference_value;
          const unit = this.variant.unit_price_measurement.reference_unit;

          valueEl.innerHTML = this.data.formatted[this.variant.id].unitPrice;
          unitEl.textContent = value === 1 ? unit : `${value} ${unit}`;
        }

        unitPriceEl.hidden = !this.variant.unit_price_measurement;
        this.price.classList.toggle(
          'price--on-sale',
          this.variant.compare_at_price > this.variant.price
        );
        this.price.classList.toggle('price--sold-out', !this.variant.available && !this.preSelection);
      }

      this.price.querySelector('.price__default').hidden = !this.variant && !this.preSelection;
      this.price.querySelector('.price__no-variant').hidden = this.variant || this.preSelection;
      const from = this.price.querySelector('.price__from');
      if (from) {
        from.hidden = !this.preSelection;
      }
    }

    /**
     * Updates the sale percent badge next to the PDP price.
     * @param {number} priceCents - Current displayed price in cents.
     * @param {number} compareAtCents - Price to compare against in cents.
     */
    updateSaleDiscountBadge(priceCents, compareAtCents) {
      const badge = this.price?.querySelector('.price__discount-badge');
      if (!badge) return;

      const price = Number(priceCents || 0);
      const compareAt = Number(compareAtCents || 0);
      const percent = compareAt > price && price > 0
        ? Math.round(((compareAt - price) * 100) / compareAt)
        : 0;

      badge.hidden = percent <= 0;
      const value = badge.querySelector('.js-value');
      if (value) value.textContent = percent > 0 ? `${percent}% Off` : '';
    }

    /**
     * Updates the weight.
     */
    updateWeight() {
      this.weights = this.weights || this.section.querySelectorAll('.product-info__weight');
      if (this.weights.length === 0) return;

      const weightAvailable = this.variant && this.variant.weight > 0;
      this.weights.forEach((weight) => {
        weight.textContent = weightAvailable ? this.data.formatted[this.variant.id].weight : '';
        weight.hidden = !weightAvailable;
      });
    }

    /**
     * Updates the Barcode.
     */
    updateBarcode() {
      this.barcodes = this.barcodes || this.section.querySelectorAll('.product-info__barcode-value');
      if (this.barcodes.length === 0) return;

      const barcodeAvailable = this.variant && this.variant.barcode;
      this.barcodes.forEach((barcode) => {
        barcode.textContent = barcodeAvailable ? this.variant.barcode : '';
        barcode.parentNode.hidden = !barcodeAvailable;
      });
    }

    /**
     * Updates the SKU.
     */
    updateSku() {
      this.sku = this.sku || this.section.querySelector('.product-sku__value');
      if (!this.sku) return;

      const skuAvailable = this.variant && this.variant.sku;
      this.sku.textContent = skuAvailable ? this.variant.sku : '';
      this.sku.parentNode.hidden = !skuAvailable;
    }

    /**
     * Updates the url with the selected variant id.
     * @param {object} evt - Event object.
     */
    updateUrl(evt) {
      if (!evt || evt.type !== 'change' || this.dataset.updateUrl === 'false') return;
      window.history.replaceState({}, '', `${this.dataset.url}?variant=${this.variant.id}`);
    }

    /**
     * Updates the value of the hidden [name="id"] form inputs.
     */
    updateVariantInput() {
      this.forms =
        this.forms || this.section.querySelectorAll('.js-product-form-main, .js-instalments-form');

      this.forms.forEach((form) => {
        const input = form.querySelector('input[name="id"]');
        input.value = this.variant.id;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }

    /**
     * Gets the selected option element from each selector.
     * @returns {Array}
     */
    getSelectedOptions() {
      const selectedOptions = [];

      this.optionSelectors.forEach((selector) => {
        if (selector.dataset.selectorType === 'dropdown') {
          const selectedOption = selector.querySelector('.custom-select__option[aria-selected="true"]');
          const selectedValue = selectedOption?.dataset.value || '';
          selectedOptions.push(selectedValue === '' ? null : selectedValue);
        } else {
          const selected = selector.querySelector('input:checked');
          selectedOptions.push(selected ? selected.value : null);
        }
      });

      return selectedOptions;
    }

    /**
     * Gets the product data.
     * @returns {?object}
     */
    getProductData() {
      const dataEl = this.querySelector('[type="application/json"]');
      return JSON.parse(dataEl.textContent);
    }

    /**
     * Get selected variant data.
     * @returns {?object} Variant object, or null if one is not selected.
     */
    getSelectedVariant() {
      const selectedOptions = this.getSelectedOptions();
      return this.data.product.variants.find(
        (v) => v.options.every((val, index) => val === selectedOptions[index])
      );
    }
  }

  customElements.define('variant-picker', VariantPicker);
}
